import React, { useMemo, useState } from "react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from "recharts";
import { Typography, Box, useTheme, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, CircularProgress, Alert } from "@mui/material";
import dayjs from "dayjs";
import { Currencies, CURRENCY, Money } from "../../../../utils/money";
import { BalanceHistoryPeriod } from "../../../../interfaces/common/wallet";
import FilterChips from "../../../common/filter-chips/filter-chips";

interface BalanceHistoryChartProps {
  historyData: {
    balance: Money[];
    timestamp: string;
  }[];
  period: BalanceHistoryPeriod;
  onPeriodSelected: (period: BalanceHistoryPeriod) => void;
  loading: boolean;
}


interface ChartDataPoint {
  timestamp: string;
  [currency: string]: number | string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    dataKey: string;
    value: number;
    color: string;
    payload: ChartDataPoint;
  }>;
  label?: string;
}

const FINANCIAL_COLORS = new Map([
    ["USD", "green"],
    ["LBP", "blue"],
    ["EUR", "purple"],
])


// Custom tooltip for financial data
const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          boxShadow: 2,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Typography 
            key={entry.dataKey} 
            variant="body2"
            sx={{ 
              color: entry.color,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            <span>{entry.dataKey}:</span>
            <span>
              {entry.value.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </span>
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

export const BalanceHistoryChart = ({ historyData, onPeriodSelected, loading, period }: BalanceHistoryChartProps) => {
  const theme = useTheme();
  const [selectedCurrencies, setSelectedCurrencies] = useState<CURRENCY[]>([CURRENCY.USD]);

  const handlePeriodChange = (event: SelectChangeEvent) => {
    const newPeriod = event.target.value as BalanceHistoryPeriod;
    onPeriodSelected(newPeriod);
  };

  // Transform backend data into chart-friendly format
  const chartData = useMemo((): ChartDataPoint[] => {
    const data = historyData.map((snapshot) => {
      const point: ChartDataPoint = {
        timestamp: dayjs(snapshot.timestamp).format("MM/DD HH:mm"),
      };
      
      selectedCurrencies.forEach((c)=>{
        const val = snapshot.balance.find(v => v[0] == c);
        point[c] =  val ? parseFloat(val[1].toString()) : "0"
      })

      return point
    })

    return data;
  }, [historyData, selectedCurrencies]);

  // Determine all currencies in the data
  const currencies = useMemo((): string[] => {
    if (!historyData.length) return [];
    return Array.from(new Set(historyData.flatMap((s) => s.balance.map(([c]) => c))));
  }, [historyData]);

  if (!historyData.length) {
    return (
      <Box sx={{ 
        width: "100%", 
        height: 400, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1
      }}>
        <Typography variant="body1" color="text.secondary">
          No historical data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Period Selector */}

      {/* Chart */}
      <Box sx={{ 
        width: "100%", 
        height: 400,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        backgroundColor: 'background.paper'
      }}>
         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>

            <FilterChips 
                label={""}
                options={[CURRENCY.USD, CURRENCY.LBP]} 
                selected={selectedCurrencies} 
                onChange={(s)=>setSelectedCurrencies(s)}
                multiple
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="period-select-label">Period</InputLabel>
            <Select
                labelId="period-select-label"
                id="period-select"
                value={period}
                label="Period"
                onChange={handlePeriodChange}
            >
                <MenuItem value={BalanceHistoryPeriod.LAST_24_HOURS}>24 Hours</MenuItem>
                <MenuItem value={BalanceHistoryPeriod.LAST_WEEK}>1 Week</MenuItem>
                <MenuItem value={BalanceHistoryPeriod.LAST_MONTH}>1 Month</MenuItem>
                <MenuItem value={BalanceHistoryPeriod.LAST_YEAR}>1 Year</MenuItem>
            </Select>
            </FormControl>
        </Box>
        <ResponsiveContainer width="100%" height="100%">
          {loading ? <Box justifyContent={"center"} alignItems={"center"} display={"flex"} width={"100%"}><CircularProgress /></Box> : 
            chartData.length == 0 ? <Alert severity="info">No data to show</Alert> :
          <LineChart 
            data={chartData} 
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid 
              stroke={theme.palette.divider} 
              strokeDasharray="3 3" 
              vertical={false}
            />
            <XAxis 
              dataKey="timestamp" 
              tick={{ 
                fontSize: 12,
                fill: theme.palette.text.secondary
              }}
              axisLine={{ stroke: theme.palette.divider }}
              tickLine={{ stroke: theme.palette.divider }}
            />
            <YAxis 
              tick={{ 
                fontSize: 12,
                fill: theme.palette.text.secondary
              }}
              axisLine={{ stroke: theme.palette.divider }}
              tickLine={{ stroke: theme.palette.divider }}
              tickFormatter={(value) => value.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              })}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{
                paddingTop: 10,
                paddingBottom: 20,
              }}
            />
            {Currencies.map((currency, idx) => (
              <Line
                key={currency}
                type="monotone"
                dataKey={currency}
                stroke={FINANCIAL_COLORS.get(currency) ?? "success"}
                dot={false}
                strokeWidth={2.5}
                activeDot={{ 
                  r: 6, 
                  stroke: FINANCIAL_COLORS.get(currency) ?? "success",
                  strokeWidth: 2,
                  fill: theme.palette.background.paper
                }}
                connectNulls={true}
              />
            ))}
          </LineChart>}
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};